import React, { MouseEvent, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';

import classnames from 'classnames/bind';

import COLOR from '@CONSTANTS/COLOR.constant';

import CircularProgressIndicator from '@COMPONENTS/SHARED/CircularProgressIndicator';
import Tooltip from '@COMPONENTS/SHARED/Tooltip';
import Lock from '@COMPONENTS/SHARED/Lock';

import { ReactComponent as UserProfileIcon } from '@ICONS/user-profile-icon.svg';
import { ReactComponent as AddPlusIcon } from '@ICONS/add-plus-icon.svg';
import { Trash2, Upload, Download, AlertCircle, Edit2 } from 'lucide-react';

import styles from './BasicButton.module.scss';

const cx: CX = classnames.bind(styles);

const icons: { [key: string]: any } = {
    'user-profile': UserProfileIcon,
    'add-plus': AddPlusIcon,
    trash: Trash2,
    upload: Upload,
    download: Download,
    help: AlertCircle,
    edit: Edit2,
};

function BasicButton(props: Props) {
    const {
        tooltip,
        locked,
        title,
        style,
        variant,
        size,
        icon,
        progressIndicatorColor,
        progressIndicatorSize,
        isProcessing,
        onClick,
    } = props;

    const [tooltipId] = useState(Tooltip.getId());

    function handleClick(event: MouseEvent<HTMLButtonElement>) {
        if (!isProcessing && !locked) {
            onClick(event);
        }
    }

    // Determine the CSS class based on variant
    const getVariantClass = () => {
        switch (variant) {
            case 'secondary':
                return 'fancy-btn-secondary';
            case 'neutral':
                return 'fancy-btn-neutral';
            case 'success':
                return 'fancy-btn-success';
            case 'warning':
                return 'fancy-btn-warning';
            case 'danger':
                return 'fancy-btn-danger';
            case 'orange':
                return 'fancy-btn-orange';
            case 'primary':
            default:
                return 'fancy-btn-primary';
        }
    };

    const getSizeClass = () => {
        switch (size) {
            case 'small':
                return 'fancy-btn-small';
            case 'large':
                return 'fancy-btn-large';
            case 'medium':
            default:
                return 'fancy-btn-medium';
        }
    };

    return (
        <>
            <button
                id={tooltipId}
                type="button"
                style={style}
                className={cx('basic-button', 'clear-button', getVariantClass(), getSizeClass(), {
                    'is-processing': isProcessing,
                    locked,
                })}
                onClick={handleClick}
            >
                {
                    locked
                    && (
                        <div className={cx('lock-wrapper')}>
                            <Lock size="small" />
                        </div>
                    )
                }

                {
                    !isProcessing
                        ? (
                            <div className={cx('button-content')}>
                                {icon && icons[icon] && (
                                    <div className={cx('icon-wrapper')}>
                                        {React.createElement(icons[icon], { width: 16, height: 16 })}
                                    </div>
                                )}
                                <span className={cx('title-text')}>{title}</span>
                            </div>
                        )
                        : (
                            <div className={cx('processing')}>
                                <CircularProgressIndicator
                                    color={progressIndicatorColor}
                                    size={progressIndicatorSize}
                                />
                            </div>
                        )
                }
            </button>
            {
                tooltip
                && (
                    <Tooltip
                        id={tooltipId}
                        content={tooltip}
                        disabled={false}
                    />
                )
            }
        </>
    );
}

BasicButton.defaultProps = {
    tooltip: '',
    locked: false,
    isProcessing: false,
    variant: 'primary',
    size: 'medium',
    style: {},
    icon: undefined,
    progressIndicatorColor: COLOR['app-white'],
    progressIndicatorSize: 22,
};

BasicButton.propTypes = {
    tooltip: PropTypes.string,
    locked: PropTypes.bool,
    title: PropTypes.string.isRequired,
    variant: PropTypes.oneOf(['primary', 'secondary', 'neutral', 'success', 'warning', 'danger', 'orange']),
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    style: PropTypes.shape({}),
    icon: PropTypes.oneOf(['user-profile', 'add-plus', 'trash', 'upload', 'download', 'help', 'edit']),
    isProcessing: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    progressIndicatorColor: PropTypes.string,
    progressIndicatorSize: PropTypes.number,
};

interface Props {
    tooltip?: string;
    locked?: boolean;
    title: string;
    variant?: 'primary' | 'secondary' | 'neutral' | 'success' | 'warning' | 'danger' | 'orange';
    size?: 'small' | 'medium' | 'large';
    style?: React.CSSProperties;
    icon?: 'user-profile' | 'add-plus' | 'trash' | 'upload' | 'download' | 'help' | 'edit';
    isProcessing?: boolean;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    progressIndicatorColor?: string;
    progressIndicatorSize?: number;
}

export default BasicButton;
